import { numFont } from '../../../utils/fonts';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import axios from 'axios';

const DEBOUNCE_MS = 300;

/** "Lähete #17 · Tili Oy" for an open deferred parts sale row. */
export function laheteLabel(t, sale) {
  if (!sale) return '';
  const number = t('orderBuilder.laheteNumber', { number: sale.number });
  return sale.customer_name ? `${number} · ${sale.customer_name}` : number;
}

/**
 * Open lähetteet (deferred parts sales, status open) typeahead: number or customer name.
 * `onChange` carries the sale row (id, number, customer_name) or null.
 */
function LahetePicker({ value, onChange, label, size = 'small', autoFocus = false, sx, testId = 'lahete-picker' }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef(null);
  const requestRef = useRef(0);

  const search = (term) => {
    window.clearTimeout(debounceRef.current);
    const requestId = ++requestRef.current;
    setLoading(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const params = { mode: 'deferred', status: 'open', page_size: 20 };
        const trimmed = term.replace('#', '').trim();
        if (trimmed) {
          if (/^\d+$/.test(trimmed)) params.number = trimmed;
          else params.search = trimmed;
        }
        const response = await axios.get('/api/parts-sales/', { params });
        if (requestId !== requestRef.current) return;
        const rows = response.data?.results || response.data || [];
        setOptions(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (requestId === requestRef.current) setOptions([]);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    }, term ? DEBOUNCE_MS : 0);
  };

  useEffect(() => {
    search('');
    return () => window.clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Autocomplete
      size={size}
      sx={sx}
      options={options}
      value={value || null}
      inputValue={inputValue}
      loading={loading}
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={(sale) => laheteLabel(t, sale)}
      renderOption={(props, sale) => (
        <li {...props} key={sale.id} data-testid={`${testId}-option-${sale.id}`}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ ...numFont, fontWeight: 600 }}>
              {t('orderBuilder.laheteNumber', { number: sale.number })}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {sale.customer_name || ''}
            </Typography>
          </Box>
        </li>
      )}
      onInputChange={(_, val, reason) => {
        setInputValue(val);
        if (reason === 'input') search(val);
      }}
      onChange={(_, val) => onChange(val)}
      noOptionsText={loading ? t('orderBuilder.searching') : t('orderBuilder.noOpenLahetteet')}
      data-testid={testId}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t('orderBuilder.lahetePlaceholder')}
          autoFocus={autoFocus}
          inputProps={{ ...params.inputProps, 'data-testid': `${testId}-input` }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default LahetePicker;
